import { Component, OnDestroy, OnInit } from '@angular/core';
import { TableResponse } from '../../../models/table/table-response.model';
import { TableLazyLoadEvent } from 'primeng/table';
import { mapLazyLoadEvent } from '../../../models/table/table-request.model';
import { UserManagementFacadeService } from '../../../services/management/user-management-facade.service';
import { User } from '../../../store/authentication/model/user.model';
import { combineLatestWith, filter, map, Observable, Subscription, tap } from 'rxjs';
import { DateUtilsService } from '../../../services/core/date-utils.service';
import { UserManagementService } from '../../../services/management/user-management.service';
import { TableConfig } from '../../../models/table/table-config.model';
import { CreateUserModalComponent } from '../user-modal/create-user-modal/create-user-modal.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { TableDataSource } from '../../../models/table/table-data-source.interface';
import { MessageToUserModalComponent } from '../message-to-user-modal/message-to-user-modal.component';
import { UserMessageDTO } from '../../../models/user-management/user-message-dto.model';

@Component({
    selector: 'app-test-wrapper',
    templateUrl: './user-table-wrapper.component.html',
    styleUrl: './user-table-wrapper.component.scss'
})
export class UserTableWrapperComponent implements OnInit, OnDestroy {
    protected readonly DateUtilsService = DateUtilsService;

    data: Observable<TableResponse<User>> = this.userManagementFacadeService.tableData$.pipe(
        tap(() => {
            this.userTableConfig.loading = false;
        })
    );

    table$: Observable<{ showSelectedUsers: boolean; tableData: TableResponse<User> }> = this.userManagementFacadeService.showSelectedUsers$.pipe(
        combineLatestWith(this.data),
        map(([showSelectedUsers, tableData]) => {
            return { showSelectedUsers: showSelectedUsers, tableData: tableData };
        }),
        tap((data) => {
            this.userTableConfig.paginator = !data.showSelectedUsers;
            this.userTableConfig.rows = !data.showSelectedUsers ? 15 : data.tableData.totalRecords;
            this.userTableConfig.alwaysShowPaginator = !data.showSelectedUsers;
            this.userTableConfig.showSelected = data.showSelectedUsers;
            this.userTableDataSource!.tableData = data.tableData;
        })
    );

    userTableConfig!: TableConfig<User>;
    userTableDataSource: TableDataSource<User> | undefined;

    createUserModal: DynamicDialogRef | undefined;
    createUserModal$: Subscription = new Subscription();

    messageToUserModal: DynamicDialogRef | undefined;
    messageToUserModal$: Subscription = new Subscription();

    constructor(
        private userManagementService: UserManagementService,
        private userManagementFacadeService: UserManagementFacadeService,
        public dateUtilsService: DateUtilsService,
        private translate: TranslateService,
        private dialogService: DialogService,
        private messageService: MessageService
    ) {}

    ngOnInit() {
        this.userTableConfig = {
            paginator: true,
            rows: 15,
            showCurrentPageReport: true,
            alwaysShowPaginator: true,
            loading: true,
            rowsPerPageOptions: [15, 30, 50],
            globalFilterFields: ['firstName', 'userName'],
            selection: [],
            showSelected: false,
            selectionMode: 'multiple',
            toolbarConfig: {
                buttons: [
                    {
                        title: 'TABLE.BUTTON_EDIT',
                        method: this.editUser.bind(this),
                        enabled: this.isEqualOne
                    },
                    {
                        title: 'TABLE.BUTTON_DELETE',
                        method: this.deleteUser.bind(this),
                        enabled: this.isGreaterZero
                    },
                    {
                        title: 'USERMANAGEMENT.SEND_MESSAGE',
                        method: this.messageToUser.bind(this),
                        enabled: this.isGreaterZero
                    }
                ],
                clearSelection: this.clearSelection.bind(this),
                showSelection: this.showSelection.bind(this)
            }
        };

        this.userTableDataSource = {
            addData: this.createUser.bind(this),
            loadData: this.loadUsers.bind(this),
            onSelectionChange: this.onSelectionChange.bind(this),
            setGlobalFilter: undefined,
            clearGlobalFilterField: undefined,
            tableData: undefined
        };

        this.userManagementFacadeService.selectUsers$
            .pipe(
                filter((selectedUser) => selectedUser.data.length !== undefined),
                tap((selectedUser) => {
                    this.userTableConfig.selection = selectedUser.data;
                })
            )
            .subscribe();
    }

    // functions which loads the user
    loadUsers = (event$: TableLazyLoadEvent) => {
        this.userManagementFacadeService.loadUsers(mapLazyLoadEvent(event$));
    };

    onSelectionChange = (users: User[]) => {
        this.userManagementFacadeService.updateAllSelectedUsers(users);
    };

    createUser = () => {
        this.createUserModal = this.dialogService.open(CreateUserModalComponent, {
            header: this.translate.instant('USERMANAGEMENT.CREATE_USER'),
            style: {
                width: '75vw',
                height: '800px'
            },
            contentStyle: { overflow: 'auto' },
            closeOnEscape: true,
            data: null
        });

        this.createUserModal$ = this.createUserModal.onClose
            .pipe(
                tap((data) => {
                    if (data) {
                        this.messageService.add({
                            severity: data.severity,
                            summary: data.summary,
                            detail: data.detail
                        });
                    }
                })
            )
            .subscribe();
    };

    editUser = (selectedUsers: User[]) => {
        // edit when just one user is selected
        if (selectedUsers.length === 0 || selectedUsers.length > 1) {
            this.messageService.add({ severity: 'info', summary: '', detail: this.translate.instant('USERMANAGEMENT.SELECT_ONE_USER') });
        } else if (selectedUsers.length === 1) {
            this.createUserModal$ = this.userManagementService
                .getUserContact(selectedUsers[0].id!)
                .pipe(
                    combineLatestWith(this.userManagementService.getUserPayment(selectedUsers[0].id!)),
                    tap(([contact, payment]) => {
                        this.createUserModal = this.dialogService.open(CreateUserModalComponent, {
                            header: this.translate.instant('USERMANAGEMENT.CREATE_USER'),
                            style: {
                                width: '75vw',
                                height: '800px'
                            },
                            contentStyle: { overflow: 'auto' },
                            closable: true,
                            data: [contact, payment, selectedUsers[0]]
                        });
                        this.createUserModal$ = this.createUserModal.onClose
                            .pipe(
                                tap((data) => {
                                    if (data) {
                                        this.messageService.add({
                                            severity: data.severity,
                                            summary: data.summary,
                                            detail: data.detail
                                        });
                                    }
                                })
                            )
                            .subscribe();
                    })
                )
                .subscribe();
        } else {
            // multiple users cannot be edited at the same time
            this.messageService.add({ severity: 'error', summary: '', detail: 'USERMANAGEMENT.EDIT_MULTIPLE_USER' });
        }
    };

    deleteUser = (selectedUsers: User[]) => {
        if (selectedUsers.length < 1) {
            this.messageService.add({ severity: 'info', summary: '', detail: this.translate.instant('USERMANAGEMENT.NO_USER_SELECTED') });
        } else {
            // just get the user ids
            this.userManagementFacadeService.deleteUsers(this.transFromUsersArray(selectedUsers));
        }
    };

    messageToUser = (selectedUsers: User[]) => {
        if (selectedUsers.length < 1) {
            this.messageService.add({ severity: 'info', summary: '', detail: this.translate.instant('USERMANAGEMENT.NO_USER_SELECTED') });
        } else {
            this.messageToUserModal = this.dialogService.open(MessageToUserModalComponent, {
                header: this.translate.instant('USERMANAGEMENT.SEND_MESSAGE'),
                style: {
                    width: '75vw',
                    height: '800px'
                },
                contentStyle: { overflow: 'auto' },
                closable: true
            });

            this.messageToUserModal$ = this.messageToUserModal.onClose
                .pipe(
                    filter((data) => data !== false),
                    tap((data: UserMessageDTO) => {
                        if (data !== undefined) {
                            data.userAccountIds = this.userTableConfig.selection.map((user) => user.id!);
                            this.userManagementFacadeService.sendMessageToUsers(data);
                        }
                    })
                )
                .subscribe();
        }
    };

    clearSelection = () => {
        this.userManagementFacadeService.updateAllSelectedUsers([]);
    };

    showSelection = () => {
        this.userManagementFacadeService.changeUserTableData();
    };

    translateRoles(roles: string[]): string {
        return this.userManagementService.translateRoles(roles);
    }

    private transFromUsersArray(selectedUsers: User[]): number[] {
        return selectedUsers.map((user) => user.id!);
    }

    private isEqualOne = (compareValue: number): boolean => {
        return 1 === compareValue;
    };

    private isGreaterZero(compareValue: number): boolean {
        return compareValue > 0;
    }

    ngOnDestroy() {
        this.createUserModal$.unsubscribe();
        this.messageToUserModal$.unsubscribe();
    }
}
